/**
 * StaticDataService — Catalogo eager, dossier a richiesta.
 *
 * Due strati, apposta:
 *   - `virtual:authors-catalog` (vedi vite.config.ts) porta TUTTI gli autori
 *     senza le chiavi pesanti: è quello che serve a lista, filtri, grafo delle
 *     influenze e mappa dei temi, e si carica al boot.
 *   - `src/data/authors/<id>.json` è il dossier completo, un chunk per autore,
 *     caricato da `loadAuthor()` quando si apre la scheda.
 *
 * Misurato sul golden inglese: le chiavi del dossier sono ~93% di ogni JSON,
 * quindi il boot scende da 528 KB di contenuti a ~40 KB di catalogo.
 *
 * A init() il servizio inoltre:
 *   - ordina cronologicamente (birth_year crescente; i non datati per epoca via
 *     chronoYear — Beowulf, senza data ma antico, va primo);
 *   - a ogni dossier caricato riempie Passage.work_id e costruisce gli indici
 *     per opera e per passo, così i chiamanti trattano i passi come piatti.
 */

import type {
  Author,
  AuthorCatalog,
  Work,
  Passage,
  ThemeTreatmentBlurb,
  MajorWork,
} from '@/data/schema';
import type { IDataService, AuthorFilters } from './IDataService';
import { chronoYear } from '@/data/chronology';

interface AuthorIndex {
  works: Map<string, Work>;
  passages: Map<string, Passage>;
}

// Il catalogo: tutti gli autori, senza le chiavi pesanti. Modulo virtuale
// costruito a build time dai JSON stessi, quindi non può andare fuori sincrono.
import CATALOG from 'virtual:authors-catalog';

// Vite glob NON eager: ogni dossier è un chunk suo, caricato a richiesta.
// La chiave è '/src/data/authors/john-keats.json'.
const AUTHOR_MODULES = import.meta.glob<Author>(
  '/src/data/authors/*.json',
  { import: 'default' },
);

export class StaticDataService implements IDataService {
  private catalog: AuthorCatalog[] = [];
  private catalogById = new Map<string, AuthorCatalog>();
  private dossiers = new Map<string, Author>();
  private inFlight = new Map<string, Promise<Author | null>>();
  private indexes = new Map<string, AuthorIndex>();
  private initialized = false;

  async init(): Promise<void> {
    if (this.initialized) return;

    // Ordine cronologico: birth_year crescente, i non datati collocati per
    // epoca (così Beowulf — senza data ma antico — va primo, non ultimo).
    const loaded = [...CATALOG].sort(
      (a, b) => chronoYear(a).year - chronoYear(b).year,
    );

    this.catalog = loaded;
    for (const a of loaded) this.catalogById.set(a.id, a);

    this.initialized = true;
    console.info(`[data] StaticDataService ready — ${this.catalog.length} authors in catalog`);
  }

  async loadAuthor(id: string): Promise<Author | null> {
    this.assertReady();

    const cached = this.dossiers.get(id);
    if (cached) return cached;

    // Due viste che chiedono lo stesso autore insieme non devono scaricarlo due
    // volte: la promessa in volo si condivide.
    const pending = this.inFlight.get(id);
    if (pending) return pending;

    const promise = this.fetchDossier(id).finally(() => this.inFlight.delete(id));
    this.inFlight.set(id, promise);
    return promise;
  }

  private async fetchDossier(id: string): Promise<Author | null> {
    const meta = this.catalogById.get(id);
    if (!meta) {
      console.warn(`[data] loadAuthor("${id}") — id not in catalog`);
      return null;
    }

    const path = `/src/data/authors/${id}.json`;
    const load = AUTHOR_MODULES[path];
    if (!load) {
      console.warn(`[data] catalog lists "${id}" but no JSON found at ${path}`);
      return null;
    }

    const author = await load();
    this.dossiers.set(id, author);
    this.buildIndex(author);
    return author;
  }

  private buildIndex(author: Author): void {
    const works = new Map<string, Work>();
    const passages = new Map<string, Passage>();
    for (const w of author.works) {
      works.set(w.id, w);
      if (isMajor(w) && w.passages) {
        for (const p of w.passages) {
          // Populate parent reference so callers can treat passages as flat.
          if (!p.work_id) p.work_id = w.id;
          passages.set(p.id, p);
        }
      }
    }
    this.indexes.set(author.id, { works, passages });
  }

  listAuthors(filters?: AuthorFilters): AuthorCatalog[] {
    this.assertReady();
    if (!filters) return [...this.catalog];

    const needle = filters.search?.trim().toLowerCase();
    return this.catalog.filter(a => {
      if (filters.periods?.length && !filters.periods.some(p => a.periods.includes(p))) return false;
      if (filters.movements?.length && !filters.movements.some(m => a.movements.includes(m))) return false;
      if (filters.genres?.length && !filters.genres.some(g => a.genres.includes(g))) return false;
      if (needle) {
        const hay = `${a.name} ${a.tagline}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }

  getAuthor(id: string): AuthorCatalog | null {
    this.assertReady();
    return this.catalogById.get(id) ?? null;
  }

  getFullAuthor(id: string): Author | null {
    this.assertReady();
    return this.dossiers.get(id) ?? null;
  }

  getWork(authorId: string, workId: string): Work | null {
    this.assertReady();
    return this.indexes.get(authorId)?.works.get(workId) ?? null;
  }

  getPassage(authorId: string, passageId: string): Passage | null {
    this.assertReady();
    return this.indexes.get(authorId)?.passages.get(passageId) ?? null;
  }

  listPassages(authorId: string): Passage[] {
    this.assertReady();
    const idx = this.indexes.get(authorId);
    return idx ? [...idx.passages.values()] : [];
  }

  listThemeTreatments(authorId: string): ThemeTreatmentBlurb[] {
    this.assertReady();
    // Il dossier vince quando c'è: è l'unico che porta `explanation`.
    return (
      this.dossiers.get(authorId)?.theme_treatments ??
      this.catalogById.get(authorId)?.theme_treatments ??
      []
    );
  }

  listAuthorsByTheme(themeId: string): AuthorCatalog[] {
    this.assertReady();
    return this.catalog.filter(a =>
      a.theme_treatments.some(t => t.theme_id === themeId),
    );
  }

  private assertReady(): void {
    if (!this.initialized) {
      throw new Error('[data] StaticDataService used before init()');
    }
  }
}

function isMajor(w: Work): w is MajorWork {
  return w.is_major === true;
}
